import React, { useCallback, useEffect, useRef, useState } from "react";
import "./WLAskAIPanel.css";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import useHttpNoAuth from "../../hooks/use-http-no-auth";

const ListingCard = ({ listing, added, onAdd }) => {
  const images = (listing.files || [])
    .map((f) => f?.t_realmex_data_firehose_file_path)
    .filter(Boolean);
  const [imgIdx, setImgIdx] = useState(0);

  const hasImages = images.length > 0;
  const showArrows = images.length > 1;

  const prev = (e) => {
    e.stopPropagation();
    setImgIdx((i) => (i - 1 + images.length) % images.length);
  };
  const next = (e) => {
    e.stopPropagation();
    setImgIdx((i) => (i + 1) % images.length);
  };

  const { sendRequest: addListingRequest } = useHttpNoAuth();

  const addListing = useCallback(() => {
    addListingRequest(
      {
        url: `${BACKEND_URL}/realmex/assign/${listing.id}`,
        method: "PATCH",
      },
      () => {
        onAdd?.(listing);
      }
    );
  }, [listing, addListingRequest, onAdd]);

  const handleAdd = () => {
    addListing();
  };

  return (
    <div className="wl-ai-listing-card">
      <div className="wl-ai-listing-photo">
        {hasImages ? (
          <img src={images[imgIdx]} alt={listing.street || "listing"} />
        ) : (
          <div className="wl-ai-listing-photo-empty" />
        )}
        {showArrows && (
          <>
            <button
              type="button"
              className="wl-ai-listing-photo-nav wl-ai-listing-photo-nav--prev"
              onClick={prev}
              aria-label="Previous photo"
            >
              <ChevronLeftIcon />
            </button>
            <button
              type="button"
              className="wl-ai-listing-photo-nav wl-ai-listing-photo-nav--next"
              onClick={next}
              aria-label="Next photo"
            >
              <ChevronRightIcon />
            </button>
          </>
        )}
        {hasImages && (
          <div className="wl-ai-listing-photo-counter">
            <PhotoCameraOutlinedIcon className="wl-ai-listing-photo-counter-icon" />
            <span>
              {imgIdx + 1}/{images.length}
            </span>
          </div>
        )}
      </div>
      <div className="wl-ai-listing-body">
        <div>
          <div className="wl-ai-listing-street">{listing.title}</div>
          <div className="wl-ai-listing-city">{listing.city}</div>
          {listing.price_main && (
            <div className="wl-ai-listing-price">{listing.price_main}</div>
          )}
          {listing.reason && (
            <div className="wl-ai-listing-reason">
              <AutoAwesomeIcon className="wl-ai-listing-reason-icon" />
              <span>{listing.reason}</span>
            </div>
          )}
        </div>
        <button
          type="button"
          className={`wl-ai-listing-add${added ? " is-added" : ""}`}
          onClick={handleAdd}
          disabled={added}
        >
          {added ? <CheckIcon /> : <AddIcon />}
          <span>{added ? "Added to my listings" : "Add to my listings"}</span>
        </button>
      </div>
    </div>
  );
};

const ListingPager = ({ listings, addedIds, onAdd }) => {
  const [idx, setIdx] = useState(0);
  const total = listings.length;

  useEffect(() => {
    setIdx(0);
  }, [listings]);

  if (total === 0) return null;

  const safeIdx = Math.min(idx, total - 1);
  const current = listings[safeIdx];
  const goPrev = () => setIdx((i) => (i - 1 + total) % total);
  const goNext = () => setIdx((i) => (i + 1) % total);

  return (
    <div className="wl-ai-listing-pager">
      <ListingCard
        key={current.id}
        listing={current}
        added={addedIds.has(current.id)}
        onAdd={onAdd}
      />
      {total > 1 && (
        <div className="wl-ai-listing-pager-controls">
          <button
            type="button"
            className="wl-ai-listing-pager-btn"
            onClick={goPrev}
            aria-label="Previous listing"
          >
            <ChevronLeftIcon />
          </button>
          <div className="wl-ai-listing-pager-count">
            {safeIdx + 1} / {total}
          </div>
          <button
            type="button"
            className="wl-ai-listing-pager-btn"
            onClick={goNext}
            aria-label="Next listing"
          >
            <ChevronRightIcon />
          </button>
        </div>
      )}
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const EXAMPLE_QUERIES = [
  "Family home, energy label A/B",
  "Ground floor, ready to move in",
  "Under €600k in Utrecht",
  "Canal view, freehold",
];


const WLAskAIPanel = ({ open, onClose, onSubmit, onAdd, initialQuery }) => {
  const [value, setValue] = useState("");
  const [foundListings, setFoundListings] = useState(null);
  const [addedIds, setAddedIds] = useState(() => new Set());
  const inputRef = useRef(null);
  const lastAutoQueryRef = useRef(null);

  const handleAdd = useCallback(
    (listing) => {
      setAddedIds((prev) => {
        const next = new Set(prev);
        next.add(listing.id);
        return next;
      });
      onAdd?.(listing);
    },
    [onAdd]
  );

  const {
    sendRequest: getAIResponseRequest,
    isLoading: getAIResponseLoading,
    error: getAIResponseError,
  } = useHttpNoAuth();

  const performSearch = useCallback(
    (queryArg) => {
      const trimmed = (queryArg ?? value).trim();
      if (!trimmed) return;

      setFoundListings(null);
      getAIResponseRequest(
        {
          url: `${BACKEND_URL}/realmex/ai-search?question=${encodeURIComponent(trimmed)}`,
        },
        (data) => {
          setFoundListings(data?.listings || []);
          onSubmit?.(trimmed, data);
        }
      );
    },
    [value, getAIResponseRequest, onSubmit]
  );

  const getAIResponse = useCallback(() => {
    performSearch();
  }, [performSearch]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleKey);
    inputRef.current?.focus();
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      lastAutoQueryRef.current = null;
      return;
    }
    const trimmed = (initialQuery || "").trim();
    if (!trimmed || lastAutoQueryRef.current === trimmed) return;
    lastAutoQueryRef.current = trimmed;
    setValue(trimmed);
    setAddedIds(new Set());
    performSearch(trimmed);
  }, [open, initialQuery, performSearch]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!value.trim() || getAIResponseLoading) return;
    getAIResponse();
  };

  return (
    <div className="wl-ai-panel-overlay" onClick={onClose}>
      <div className="wl-ai-panel" onClick={(e) => e.stopPropagation()}>
        <div className="wl-ai-panel-header">
          <div className="wl-ai-panel-eyebrow">
            <AutoAwesomeIcon className="wl-ai-panel-eyebrow-icon" />
            <span>AI SEARCH</span>
          </div>
          <button className="wl-ai-panel-close" type="button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <h2 className="wl-ai-panel-title">What are you looking for?</h2>
        <p className="wl-ai-panel-subtitle">
          Describe what kind of property you are looking for.
        </p>

        <form className="wl-ai-panel-form" onSubmit={handleSubmit}>
          <div className="wl-ai-panel-input-wrap">
            <textarea
              ref={inputRef}
              className="wl-ai-panel-input"
              placeholder="e.g. 3-bedroom, energy-efficient, walking distance to the canals"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              rows={2}
              disabled={getAIResponseLoading}
            />
          </div>
          <button
            className="wl-ai-panel-send"
            type="submit"
            aria-label="Send"
            disabled={getAIResponseLoading || !value.trim()}
          >
            <NearMeOutlinedIcon />
          </button>
        </form>

        {!getAIResponseLoading && !foundListings && (
          <div className="wl-ai-panel-examples">
            <div className="wl-ai-panel-examples-label">Try one of these</div>
            <div className="wl-ai-panel-examples-list">
              {EXAMPLE_QUERIES.map((query) => (
                <button
                  key={query}
                  type="button"
                  className="wl-ai-panel-example-chip"
                  onClick={() => setValue(query)}
                >
                  {query}
                </button>
              ))}
            </div>
          </div>
        )}

        {getAIResponseLoading && (
          <div className="wl-ai-panel-results" aria-live="polite">
            <div className="wl-ai-panel-status">
              <AutoAwesomeIcon className="wl-ai-panel-status-icon" />
              <span>Fetching AI results…</span>
            </div>
            <div className="wl-ai-panel-skeleton-card">
              <div className="wl-ai-panel-skeleton-thumb" />
              <div className="wl-ai-panel-skeleton-lines">
                <div className="wl-ai-panel-skeleton-line wl-ai-panel-skeleton-line--lg" />
                <div className="wl-ai-panel-skeleton-line wl-ai-panel-skeleton-line--md" />
                <div className="wl-ai-panel-skeleton-line wl-ai-panel-skeleton-line--sm" />
                <div className="wl-ai-panel-skeleton-reason" />
                <div className="wl-ai-panel-skeleton-pill" />
              </div>
            </div>
          </div>
        )}

        {!getAIResponseLoading && getAIResponseError && (
          <div className="wl-ai-panel-results">
            <div className="wl-ai-panel-error">
              Something went wrong while fetching results. Please try again.
            </div>
          </div>
        )}

        {!getAIResponseLoading && !getAIResponseError && foundListings && (
          <div className="wl-ai-panel-results">
            {foundListings.length === 0 ? (
              <div className="wl-ai-panel-result-text">
                No listings matched your search.
              </div>
            ) : (
              <>
                <div className="wl-ai-panel-result-count">
                  {foundListings.length}{" "}
                  {foundListings.length === 1 ? "listing" : "listings"} found
                </div>
                <ListingPager
                  listings={foundListings}
                  addedIds={addedIds}
                  onAdd={handleAdd}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WLAskAIPanel;
